import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { supabase } from "@/lib/supabase";
import styles from "./onboarding.module.css";

interface ProfilePreferencesStepProps {
	onNext: (data: any) => void;
	onBack: () => void;
	initialData?: any;
}

const additionalSpecialtiesOptions = [
	"International Board Certified Lactation Consultant (IBCLC - 1000+ hour certification)",
	"Lactation Specialist/Counselor (50+ hour Certification)",
	"Massage/Bodyworker",
	"Yoga Teacher",
	"Belly binding",
	"Rebozo",
	"Spinning Babies",
	"Hypnobirthing",
	"Birth Photography",
	"Unmedicated Births",
	"Postpartum Chef",
	"Sleep consultant",
	"Childbirth Educator",
];

const genderOptions = ["Female", "Male", "Non-Binary", "Transgender"];

const sexualityOptions = ["Queer", "Poly", "Heterosexual", "Prefer not to say"];

const raceOptions = [
	"Caucasian",
	"Black",
	"Hispanic",
	"Native American",
	"Pacific Islander",
	"South Asian",
	"East Asian",
	"Prefer not to say",
];

const religionOptions = [
	"Christian",
	"Muslim",
	"Hindu",
	"Jewish",
	"Buddhist",
	"Prefer not to say",
];

const styleOptions = [
	"practical",
	"spiritual",
	"mindful",
	"warm",
	"assertive",
	"humorous",
	"holistic",
	"open-minded",
	"direct",
	"earthy",
	"affirming",
	"empowering",
	"motivational",
	"compassionate",
	"creative",
	"analytical",
	"energetic",
	"encouraging",
];

const doulaServiceTypeOptions = [
	"Birth Doula",
	"Daytime Postpartum Doula",
	"Overnight Postpartum Doula",
];

const languagesOptions = [
	"English",
	"Spanish",
	"Albanian",
	"American Sign Language",
	"Arabic",
	"Armenian",
	"Assamese",
	"Bengali",
	"Bosnian",
	"Bulgarian",
	"Cantonese",
	"Croatian",
	"Danish",
	"Dutch",
	"Farsi",
	"Flemish",
	"French",
	"Fuzhounese",
	"German",
	"Greek",
	"Gujarati",
	"Haitian Creole",
	"Hebrew",
	"Hindi",
	"Hmong",
	"Hungarian",
	"Icelandic",
	"Igbo",
	"Italian",
	"Japanese",
	"Kashmiri",
	"Korean",
	"Kurdish",
	"Malayalam",
	"Mandarin",
	"Marathi",
	"Norwegian",
	"Polish",
	"Portuguese",
	"Punjabi",
	"Romanian",
	"Russian",
	"Serbian",
	"Shona",
	"Somali",
	"Swahili",
	"Tagalog",
	"Thai",
	"Turkish",
	"Twi",
	"Ukrainian",
	"Urdu",
	"Vietnamese",
	"Yiddish",
	"Yoruba",
];

export const ProfilePreferencesStep = ({
	onNext,
	onBack,
	initialData,
}: ProfilePreferencesStepProps) => {
	const [additionalSpecialties, setAdditionalSpecialties] = useState<string[]>(
		initialData?.additionalSpecialties || [],
	);
	const [gender, setGender] = useState(initialData?.gender || "");
	const [sexuality, setSexuality] = useState(initialData?.sexuality || "");
	const [race, setRace] = useState(initialData?.race || "");
	const [religion, setReligion] = useState(initialData?.religion || "");
	const [style, setStyle] = useState<string[]>(initialData?.style || []);
	const [doulaServiceType, setDoulaServiceType] = useState<string[]>(
		initialData?.doulaServiceType || [],
	);
	const [languagesSpoken, setLanguagesSpoken] = useState<string[]>(
		initialData?.languagesSpoken || [],
	);
	const [loading, setLoading] = useState(false);

	const handleAddAdditionalSpecialty = (specialty: string) => {
		if (!additionalSpecialties.includes(specialty)) {
			setAdditionalSpecialties([...additionalSpecialties, specialty]);
		}
	};

	const handleRemoveAdditionalSpecialty = (specialty: string) => {
		setAdditionalSpecialties(
			additionalSpecialties.filter((s) => s !== specialty),
		);
	};

	const handleAddStyle = (styleItem: string) => {
		if (!style.includes(styleItem)) {
			setStyle([...style, styleItem]);
		}
	};

	const handleRemoveStyle = (styleItem: string) => {
		setStyle(style.filter((s) => s !== styleItem));
	};

	const handleAddServiceType = (serviceType: string) => {
		if (!doulaServiceType.includes(serviceType)) {
			setDoulaServiceType([...doulaServiceType, serviceType]);
		}
	};

	const handleRemoveServiceType = (serviceType: string) => {
		setDoulaServiceType(doulaServiceType.filter((s) => s !== serviceType));
	};

	const handleAddLanguage = (language: string) => {
		if (!languagesSpoken.includes(language)) {
			setLanguagesSpoken([...languagesSpoken, language]);
		}
	};

	const handleRemoveLanguage = (language: string) => {
		setLanguagesSpoken(languagesSpoken.filter((l) => l !== language));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		setLoading(true);

		try {
			// Call the edge function to save progress
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-doula-profile`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						step: "profile_preferences",
						data: {
							profile_preferences: {
								additional_specialties: additionalSpecialties,
								gender,
								sexuality,
								race,
								religion,
								style,
								doula_service_type: doulaServiceType,
								languages_spoken: languagesSpoken,
							},
						},
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				throw new Error(result.error || "Failed to save profile preferences");
			}

			onNext({
				additionalSpecialties,
				gender,
				sexuality,
				race,
				religion,
				style,
				doulaServiceType,
				languagesSpoken,
			});
		} catch (error) {
			console.error("Error saving profile preferences:", error);
			alert("Failed to save profile preferences. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.6 }}
			className={styles.wrapper}
		>
			{/* Progress Bar */}
			<div className={styles.progressSection}>
				<div className={styles.progressHeader}>
					<span className={styles.progressLabel}>Step 5 of 15</span>
					<span className={styles.progressLabel}>Profile Preferences</span>
				</div>
				<div className={styles.progressTrack}>
					<div className={styles.progressFill} style={{ width: "33%" }} />
				</div>
			</div>

			<div className="text-center space-y-2 mb-6">
				<h2 className="text-3xl font-bold text-newTheme-black font-karla">
					Profile Preferences
				</h2>
				<p className="text-newTheme-black/60 font-karla">
					Help parents find the right doula by sharing your background and style
				</p>
			</div>

			<form onSubmit={handleSubmit} className="space-y-6">
				{/* Additional Specialties */}
				<Card>
					<CardHeader>
						<CardTitle className="text-lg font-karla">
							Additional Specialties
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<Select onValueChange={handleAddAdditionalSpecialty}>
							<SelectTrigger className="bg-newTheme-white border border-newTheme-lightBlue shadow-sm">
								<SelectValue placeholder="Select additional specialties..." />
							</SelectTrigger>
							<SelectContent className="bg-white">
								{additionalSpecialtiesOptions
									.filter((option) => !additionalSpecialties.includes(option))
									.map((option) => (
										<SelectItem key={option} value={option}>
											{option}
										</SelectItem>
									))}
							</SelectContent>
						</Select>
						{additionalSpecialties.length > 0 && (
							<div className="flex flex-wrap gap-2">
								{additionalSpecialties.map((specialty) => (
									<Badge
										key={specialty}
										variant="secondary"
										className="text-sm"
									>
										{specialty}
										<button
											type="button"
											onClick={() => handleRemoveAdditionalSpecialty(specialty)}
											className="ml-2 hover:text-red-500"
										>
											<X className="h-3 w-3" />
										</button>
									</Badge>
								))}
							</div>
						)}
					</CardContent>
				</Card>

				{/* Gender */}
				<Card>
					<CardHeader>
						<CardTitle className="text-lg font-karla">Gender</CardTitle>
					</CardHeader>
					<CardContent>
						<Select value={gender} onValueChange={setGender}>
							<SelectTrigger className="bg-newTheme-white border border-newTheme-lightBlue shadow-sm">
								<SelectValue placeholder="Select your gender..." />
							</SelectTrigger>
							<SelectContent className="bg-white">
								{genderOptions.map((option) => (
									<SelectItem key={option} value={option}>
										{option}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</CardContent>
				</Card>

				{/* Sexuality */}
				<Card>
					<CardHeader>
						<CardTitle className="text-lg font-karla">Sexuality</CardTitle>
					</CardHeader>
					<CardContent>
						<Select value={sexuality} onValueChange={setSexuality}>
							<SelectTrigger className="bg-newTheme-white border border-newTheme-lightBlue shadow-sm">
								<SelectValue placeholder="Select your sexuality..." />
							</SelectTrigger>
							<SelectContent className="bg-white">
								{sexualityOptions.map((option) => (
									<SelectItem key={option} value={option}>
										{option}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</CardContent>
				</Card>

				{/* Race */}
				<Card>
					<CardHeader>
						<CardTitle className="text-lg font-karla">Race/Ethnicity</CardTitle>
					</CardHeader>
					<CardContent>
						<Select value={race} onValueChange={setRace}>
							<SelectTrigger className="bg-newTheme-white border border-newTheme-lightBlue shadow-sm">
								<SelectValue placeholder="Select your race/ethnicity..." />
							</SelectTrigger>
							<SelectContent className="bg-white">
								{raceOptions.map((option) => (
									<SelectItem key={option} value={option}>
										{option}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</CardContent>
				</Card>

				{/* Religion */}
				<Card>
					<CardHeader>
						<CardTitle className="text-lg font-karla">Religion</CardTitle>
					</CardHeader>
					<CardContent>
						<Select value={religion} onValueChange={setReligion}>
							<SelectTrigger className="bg-newTheme-white border border-newTheme-lightBlue shadow-sm">
								<SelectValue placeholder="Select your religion..." />
							</SelectTrigger>
							<SelectContent className="bg-white">
								{religionOptions.map((option) => (
									<SelectItem key={option} value={option}>
										{option}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</CardContent>
				</Card>

				{/* Style */}
				<Card>
					<CardHeader>
						<CardTitle className="text-lg font-karla">Doula Style</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<Select onValueChange={handleAddStyle}>
							<SelectTrigger className="bg-newTheme-white border border-newTheme-lightBlue shadow-sm">
								<SelectValue placeholder="Select your style traits..." />
							</SelectTrigger>
							<SelectContent className="bg-white">
								{styleOptions
									.filter((option) => !style.includes(option))
									.map((option) => (
										<SelectItem key={option} value={option}>
											{option}
										</SelectItem>
									))}
							</SelectContent>
						</Select>
						{style.length > 0 && (
							<div className="flex flex-wrap gap-2">
								{style.map((styleItem) => (
									<Badge
										key={styleItem}
										variant="secondary"
										className="text-sm"
									>
										{styleItem}
										<button
											type="button"
											onClick={() => handleRemoveStyle(styleItem)}
											className="ml-2 hover:text-red-500"
										>
											<X className="h-3 w-3" />
										</button>
									</Badge>
								))}
							</div>
						)}
					</CardContent>
				</Card>

				{/* Service Type */}
				<Card>
					<CardHeader>
						<CardTitle className="text-lg font-karla">Service Types</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<Select onValueChange={handleAddServiceType}>
							<SelectTrigger className="bg-newTheme-white border border-newTheme-lightBlue shadow-sm">
								<SelectValue placeholder="Select service types you offer..." />
							</SelectTrigger>
							<SelectContent className="bg-white">
								{doulaServiceTypeOptions
									.filter((option) => !doulaServiceType.includes(option))
									.map((option) => (
										<SelectItem key={option} value={option}>
											{option}
										</SelectItem>
									))}
							</SelectContent>
						</Select>
						{doulaServiceType.length > 0 && (
							<div className="flex flex-wrap gap-2">
								{doulaServiceType.map((serviceType) => (
									<Badge
										key={serviceType}
										variant="secondary"
										className="text-sm"
									>
										{serviceType}
										<button
											type="button"
											onClick={() => handleRemoveServiceType(serviceType)}
											className="ml-2 hover:text-red-500"
										>
											<X className="h-3 w-3" />
										</button>
									</Badge>
								))}
							</div>
						)}
					</CardContent>
				</Card>

				{/* Languages */}
				<Card>
					<CardHeader>
						<CardTitle className="text-lg font-karla">
							Languages Spoken
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<Select onValueChange={handleAddLanguage}>
							<SelectTrigger className="bg-newTheme-white border border-newTheme-lightBlue shadow-sm">
								<SelectValue placeholder="Select languages you speak..." />
							</SelectTrigger>
							<SelectContent className="bg-white">
								{languagesOptions
									.filter((option) => !languagesSpoken.includes(option))
									.map((option) => (
										<SelectItem key={option} value={option}>
											{option}
										</SelectItem>
									))}
							</SelectContent>
						</Select>
						{languagesSpoken.length > 0 && (
							<div className="flex flex-wrap gap-2">
								{languagesSpoken.map((language) => (
									<Badge key={language} variant="secondary" className="text-sm bg-newTheme-lime text-black">
										{language}
										<button
											type="button"
											onClick={() => handleRemoveLanguage(language)}
											className="ml-2 hover:text-red-500"
										>
											<X className="h-3 w-3" />
										</button>
									</Badge>
								))}
							</div>
						)}
					</CardContent>
				</Card>

				{/* Navigation Buttons */}
				<div className={styles.navRow}>
					<Button
						type="button"
						variant="outline"
						onClick={onBack}
						className={`px-8 ${styles.backButton}`}
					>
						Back
					</Button>
					<Button
						type="submit"
						disabled={loading}
						className={`px-8 ${styles.submitButton}`}
					>
						{loading ? (
							<div className="flex items-center">
								<div className={styles.spinner} />
								Saving...
							</div>
						) : (
							"Continue"
						)}
					</Button>
				</div>
			</form>
		</motion.div>
	);
};
